// src/lib/gewerbeSnapshot.ts
// Gewerbe-Snapshot, gleiches Muster wie src/lib/etwSnapshot.ts (siehe dort fuer
// die Begruendung des fehlenden Cross-File-Imports). Gewerbe hat ein zonen[]-Array
// (je Mietbereich areaM2/rentPerM2/vacancyPct/recoverablePct/freeRentMonthsY1/
// tiPerM2/leaseTermYears) statt eines einzelnen Flaechen-/Mietwerts.
//
// Reparatur-Hinweis: Der bisherige Export schrieb unter "input" (handleExportJSON),
// der bisherige Import las aber vom Top-Level (importJson: "d.kaufpreis"/"d.zonen"
// statt "d.input.kaufpreis"/"d.input.zonen") -- der Rundweg war dadurch bereits
// kaputt, unabhaengig von diesem Umbau (gleicher Bug-Typ wie bei EFH). Der Export
// schrieb ausserdem den BEREITS angepassten Preis (KP, inkl. priceAdjPct) als
// "kaufpreis" UND priceAdjPct zusaetzlich -- bei korrektem Import waere die
// Anpassung doppelt angewendet worden. Beides ist hier behoben: kaufpreis ist
// immer der rohe Basispreis, die Anpassung lebt ausschliesslich in
// playground.priceAdjPct.
//
// Finanzierung seit 14.10.2026 Runde 2 (DEAL-FLOW-STATUS.md "Als Naechstes nach
// Runde 1"): Zins + anfaengliche Tilgung (tilgungPct) statt Zins + Laufzeit
// (laufzeitYears) -- wie ETW/MFH. Alte gespeicherte Deals/Exporte haben noch
// "laufzeitYears" statt "tilgungPct" im JSON -- resolveGewerbeSnapshot() rechnet
// das beim Laden einmalig um (tilgungPctFromLaufzeit() unten), damit sie
// weiterhin denselben Jahresbetrag ergeben. Ein spaeteres Speichern schreibt dann
// bereits das neue Feld.

export type AnalyzerTypeId = "etw" | "mfh" | "efh" | "gewerbe" | "mixeduse";

/** confirmedFields: Phase-4 Runde 2b, Baustein 2 -- siehe etwSnapshot.ts fuer die
 *  ausfuehrliche Begruendung. Optional/abwaertskompatibel. */
export type DealSnapshotEnvelope<TInput, TPlayground> = {
  version: number;
  analyzerType: AnalyzerTypeId;
  input: TInput;
  playground: TPlayground;
  confirmedFields: string[];
};

function resolveField<T>(raw: Record<string, unknown> | undefined, key: string, fallback: T): T {
  const value = raw?.[key];
  if (value === undefined || value === null) return fallback;
  if (typeof fallback === "number") return typeof value === "number" && Number.isFinite(value) ? (value as T) : fallback;
  if (typeof fallback === "boolean") return typeof value === "boolean" ? (value as T) : fallback;
  if (typeof fallback === "string") return typeof value === "string" ? (value as T) : fallback;
  return (value as T) ?? fallback;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function resolveConfirmedFields(raw: Record<string, unknown> | undefined, key: string): string[] {
  const value = raw?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function newZoneId(): string {
  const c: any = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `z-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export type Bonitaet = "AAA" | "A" | "B" | "C";

export type GewerbeZone = {
  id: string;
  name: string;
  areaM2: number;
  rentPerM2: number;
  vacancyPct: number;
  recoverablePct: number;
  freeRentMonthsY1: number;
  tiPerM2: number;
  leaseTermYears: number;
};

export const GEWERBE_SNAPSHOT_VERSION = 1;

export type GewerbeSnapshotInput = {
  kaufpreis: number;
  zonen: GewerbeZone[];
  opexTotalPctBrutto: number;
  capexRuecklagePctBrutto: number;
  // Bewirtschaftung Pauschal/Einzeln (Baustein 5): identisches Muster wie MfhSnapshotInput
  // (src/lib/mfhSnapshot.ts) -- null = kein Modus gespeichert, Migration faellt in
  // gewerbeDealScore.ts' resolveGewerbeBewirtschaftung(). nichtUmlagefaehigeKosten (NEU,
  // €/Jahr) ersetzt im Einzeln-Modus das Zonen-recoverablePct-Modell durch einen direkten
  // Wert. Kein Baujahr/Aufzug -- § 28 II. BV gilt nicht fuer Gewerbe.
  bewirtschaftungModus: "pauschal" | "einzeln" | null;
  nichtUmlagefaehigeKosten: number;
  verwaltung: number;
  instandhaltungJeM2Jahr: number;
  capRateAssumed: number;
  bonitaetTop3: Bonitaet;
  indexiert: boolean;
  financingOn: boolean;
  /** Vereinheitlichte Darlehensregel (16.10.2026, Dominiks Vorgabe): Eigenkapital in Euro
   *  statt Beleihungsquote (LTV) -- wie MFH/EFH. Kredit = Kaufpreis − max(0, EK − NK). */
  eigenkapital: number;
  /** Migrations-Bruecke: bei einem alten Snapshot/Export (vor der Umstellung auf
   *  eigenkapital) der frueher gespeicherte ltvPct-Wert, sonst null. Aufgeloest in
   *  core/score/gewerbeDealScore.ts' resolveGewerbeEigenkapital(), sobald die Nebenkosten
   *  bekannt sind (eigenes Subsystem, hier nicht verfuegbar). */
  legacyLtvPct: number | null;
  zinsPct: number;
  tilgungPct: number;
  /** Deal-Score v2 Leerstand-Obergrenze. Alte Snapshots ohne dieses Feld gelten als "vollstaendig". */
  vermietet: "vollstaendig" | "teilweise" | "nein";
};

export type GewerbeSnapshotPlayground = {
  priceAdjPct: number;
  rentAdjPct: number;
  applyAdjustments: boolean;
};

export type GewerbeSnapshot = DealSnapshotEnvelope<GewerbeSnapshotInput, GewerbeSnapshotPlayground>;

// UI-Defaults, siehe GewerbeCheck.tsx (Beispielobjekt).
export const GEWERBE_INPUT_DEFAULTS: GewerbeSnapshotInput = {
  kaufpreis: 1_200_000,
  zonen: [
    { id: "default-z1", name: "Büro EG", areaM2: 250, rentPerM2: 18, vacancyPct: 0.05, recoverablePct: 0.85, freeRentMonthsY1: 0, tiPerM2: 50, leaseTermYears: 5 },
    { id: "default-z2", name: "Büro OG", areaM2: 350, rentPerM2: 19, vacancyPct: 0.1, recoverablePct: 0.75, freeRentMonthsY1: 1, tiPerM2: 35, leaseTermYears: 4 },
  ],
  opexTotalPctBrutto: 0.26,
  capexRuecklagePctBrutto: 0.04,
  bewirtschaftungModus: null,
  nichtUmlagefaehigeKosten: 0,
  verwaltung: 0,
  instandhaltungJeM2Jahr: 0,
  capRateAssumed: 0.065,
  bonitaetTop3: "B",
  indexiert: false,
  financingOn: true,
  eigenkapital: 960_000,
  legacyLtvPct: null,
  zinsPct: 0.045,
  tilgungPct: 0.02,
  vermietet: "vollstaendig",
};

export const GEWERBE_PLAYGROUND_DEFAULTS: GewerbeSnapshotPlayground = {
  priceAdjPct: 0,
  rentAdjPct: 0,
  applyAdjustments: true,
};

export function buildGewerbeSnapshot(
  input: GewerbeSnapshotInput,
  playground: GewerbeSnapshotPlayground,
  confirmedFields: string[] = []
): GewerbeSnapshot {
  return { version: GEWERBE_SNAPSHOT_VERSION, analyzerType: "gewerbe", input, playground, confirmedFields };
}

const VALID_BONITAET: Bonitaet[] = ["AAA", "A", "B", "C"];

function resolveZonen(raw: unknown): GewerbeZone[] {
  if (!Array.isArray(raw) || raw.length === 0) return GEWERBE_INPUT_DEFAULTS.zonen;
  return raw.map((z, i) => {
    const r = asRecord(z);
    return {
      id: typeof r.id === "string" && r.id ? r.id : newZoneId(),
      name: typeof r.name === "string" && r.name ? r.name : `Zone ${i + 1}`,
      areaM2: typeof r.areaM2 === "number" && Number.isFinite(r.areaM2) ? r.areaM2 : 0,
      rentPerM2: typeof r.rentPerM2 === "number" && Number.isFinite(r.rentPerM2) ? r.rentPerM2 : 0,
      vacancyPct: typeof r.vacancyPct === "number" && Number.isFinite(r.vacancyPct) ? clamp01(r.vacancyPct) : 0,
      recoverablePct: typeof r.recoverablePct === "number" && Number.isFinite(r.recoverablePct) ? clamp01(r.recoverablePct) : 0.8,
      freeRentMonthsY1: typeof r.freeRentMonthsY1 === "number" && Number.isFinite(r.freeRentMonthsY1)
        ? Math.max(0, Math.min(24, Math.round(r.freeRentMonthsY1))) : 0,
      tiPerM2: typeof r.tiPerM2 === "number" && Number.isFinite(r.tiPerM2) ? Math.max(0, r.tiPerM2) : 0,
      leaseTermYears: typeof r.leaseTermYears === "number" && Number.isFinite(r.leaseTermYears) ? Math.max(0.5, r.leaseTermYears) : 3,
    };
  });
}

export type ResolvedGewerbeSnapshot = {
  input: GewerbeSnapshotInput;
  playground: GewerbeSnapshotPlayground;
  /** false, wenn Kaufpreis oder ein nicht-leeres zonen[]-Array fehlt. */
  complete: boolean;
  confirmedFields: string[];
};

/**
 * Migration altgespeicherter Deals (vor 14.10.2026 Runde 2): aus Zins + Laufzeit die
 * dazu aequivalente anfaengliche Tilgungsrate ableiten, damit ein alter, mit Laufzeit
 * gespeicherter Gewerbe-Deal beim Laden denselben Jahresbetrag ergibt wie vorher.
 * Herleitung: die exakt amortisierende Annuitaet ist Rate = Darlehen x
 * Zins/(1-(1+Zins)^-Laufzeit) -- das Verhaeltnis Rate/Darlehen ist darlehens-unabhaengig,
 * Tilgung = Rate/Darlehen - Zins vereinfacht algebraisch zu Zins/((1+Zins)^Laufzeit - 1).
 */
export function tilgungPctFromLaufzeit(zinsPct: number, laufzeitYears: number): number {
  if (zinsPct <= 0) return laufzeitYears > 0 ? 1 / laufzeitYears : 0;
  const denom = Math.pow(1 + zinsPct, laufzeitYears) - 1;
  return denom > 0 ? zinsPct / denom : 0;
}

/** tilgungPct (neues Format) direkt uebernehmen, sonst aus laufzeitYears (altes Format,
 *  vor 14.10.2026 Runde 2) umrechnen, sonst der Default. */
function resolveTilgungPct(rawInput: Record<string, unknown> | undefined, zinsPctResolved: number): number {
  const direct = rawInput?.tilgungPct;
  if (typeof direct === "number" && Number.isFinite(direct)) return direct;
  const laufzeitYears = rawInput?.laufzeitYears;
  if (typeof laufzeitYears === "number" && Number.isFinite(laufzeitYears) && laufzeitYears > 0) {
    return tilgungPctFromLaufzeit(zinsPctResolved, laufzeitYears);
  }
  return GEWERBE_INPUT_DEFAULTS.tilgungPct;
}

export function resolveGewerbeSnapshot(raw: unknown): ResolvedGewerbeSnapshot {
  const r = asRecord(raw);
  const rawInput = asRecord(r.input);
  const rawPlayground = asRecord(r.playground);

  const complete =
    typeof rawInput.kaufpreis === "number" &&
    Array.isArray(rawInput.zonen) && rawInput.zonen.length > 0;

  const zinsPct = resolveField(rawInput, "zinsPct", GEWERBE_INPUT_DEFAULTS.zinsPct);
  const input: GewerbeSnapshotInput = {
    kaufpreis: resolveField(rawInput, "kaufpreis", GEWERBE_INPUT_DEFAULTS.kaufpreis),
    zonen: resolveZonen(rawInput.zonen),
    opexTotalPctBrutto: resolveField(rawInput, "opexTotalPctBrutto", GEWERBE_INPUT_DEFAULTS.opexTotalPctBrutto),
    capexRuecklagePctBrutto: resolveField(rawInput, "capexRuecklagePctBrutto", GEWERBE_INPUT_DEFAULTS.capexRuecklagePctBrutto),
    bewirtschaftungModus: rawInput.bewirtschaftungModus === "pauschal" || rawInput.bewirtschaftungModus === "einzeln" ? rawInput.bewirtschaftungModus : null,
    nichtUmlagefaehigeKosten: resolveField(rawInput, "nichtUmlagefaehigeKosten", GEWERBE_INPUT_DEFAULTS.nichtUmlagefaehigeKosten),
    verwaltung: resolveField(rawInput, "verwaltung", GEWERBE_INPUT_DEFAULTS.verwaltung),
    instandhaltungJeM2Jahr: resolveField(rawInput, "instandhaltungJeM2Jahr", GEWERBE_INPUT_DEFAULTS.instandhaltungJeM2Jahr),
    capRateAssumed: resolveField(rawInput, "capRateAssumed", GEWERBE_INPUT_DEFAULTS.capRateAssumed),
    bonitaetTop3: VALID_BONITAET.includes(rawInput.bonitaetTop3 as Bonitaet) ? (rawInput.bonitaetTop3 as Bonitaet) : GEWERBE_INPUT_DEFAULTS.bonitaetTop3,
    indexiert: resolveField(rawInput, "indexiert", GEWERBE_INPUT_DEFAULTS.indexiert),
    financingOn: resolveField(rawInput, "financingOn", GEWERBE_INPUT_DEFAULTS.financingOn),
    eigenkapital: resolveField(rawInput, "eigenkapital", GEWERBE_INPUT_DEFAULTS.eigenkapital),
    legacyLtvPct:
      (rawInput?.eigenkapital === undefined || rawInput?.eigenkapital === null) &&
      typeof rawInput?.ltvPct === "number" && Number.isFinite(rawInput.ltvPct)
        ? (rawInput.ltvPct as number)
        : null,
    zinsPct,
    tilgungPct: resolveTilgungPct(rawInput, zinsPct),
    vermietet:
      rawInput.vermietet === "teilweise" || rawInput.vermietet === "nein"
        ? rawInput.vermietet
        : "vollstaendig",
  };

  const playground: GewerbeSnapshotPlayground = {
    priceAdjPct: resolveField(rawPlayground, "priceAdjPct", resolveField(rawInput, "priceAdjPct", GEWERBE_PLAYGROUND_DEFAULTS.priceAdjPct)),
    rentAdjPct: resolveField(rawPlayground, "rentAdjPct", resolveField(rawInput, "rentAdjPct", GEWERBE_PLAYGROUND_DEFAULTS.rentAdjPct)),
    applyAdjustments: resolveField(rawPlayground, "applyAdjustments", resolveField(rawInput, "applyAdjustments", GEWERBE_PLAYGROUND_DEFAULTS.applyAdjustments)),
  };

  const confirmedFields = resolveConfirmedFields(r, "confirmedFields");

  return { input, playground, complete, confirmedFields };
}
